import * as React from "react";
import { Shuffle, ArrowRight, MapPin, Package, Archive, Target, X } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { cn } from "@/lib/utils.js";
import type { ReorgSuggestion } from "@homethyst/shared/schemas";

const typeIcons: Record<ReorgSuggestion["type"], typeof Shuffle> = {
  group_by_tag: Package,
  unused_in_active: Archive,
  unlocated: MapPin,
  empty_space: Shuffle,
  set_improvement_target: Target,
};

const typeColors: Record<ReorgSuggestion["type"], string> = {
  group_by_tag: "text-indigo-600 bg-indigo-50 border-indigo-200",
  unused_in_active: "text-teal-600 bg-teal-50 border-teal-200",
  unlocated: "text-blue-600 bg-blue-50 border-blue-200",
  empty_space: "text-purple-600 bg-purple-50 border-purple-200",
  set_improvement_target: "text-amber-700 bg-amber-50 border-amber-200",
};

const iconBgColors: Record<ReorgSuggestion["type"], string> = {
  group_by_tag: "bg-indigo-100 text-indigo-600",
  unused_in_active: "bg-teal-100 text-teal-600",
  unlocated: "bg-blue-100 text-blue-600",
  empty_space: "bg-purple-100 text-purple-600",
  set_improvement_target: "bg-amber-100 text-amber-700",
};

interface Props {
  onNavigate: (path: string) => void;
  onRefreshDashboard?: () => void;
}

export function ReorgSuggestions({ onNavigate, onRefreshDashboard }: Props) {
  const [suggestions, setSuggestions] = React.useState<ReorgSuggestion[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [acting, setActing] = React.useState<string | null>(null);
  const [dismissed, setDismissed] = React.useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem("ht_reorg_dismissed");
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });
  const toast = useToast();

  React.useEffect(() => {
    apiFetch<ReorgSuggestion[]>("/api/suggestions/reorganize")
      .then((data) => {
        setSuggestions(data.filter((s) => !dismissed.has(s.id)));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleAction = async (suggestion: ReorgSuggestion) => {
    // "Target for improvement" suggestions have no items to move — send the user
    // to the space so they can define its current/desired state (intent).
    if (suggestion.type === "set_improvement_target" && suggestion.targetSpaceId) {
      onNavigate(`/spaces/${suggestion.targetSpaceId}`);
      return;
    }
    // For suggestions with a target space, execute the bulk move
    if (suggestion.targetSpaceId && suggestion.itemIds.length > 0 && suggestion.type !== "unlocated") {
      setActing(suggestion.id);
      try {
        await apiFetch("/api/items/bulk", {
          method: "PUT",
          body: JSON.stringify({ ids: suggestion.itemIds, spaceId: suggestion.targetSpaceId }),
        });
        toast.show(`Moved ${suggestion.itemIds.length} items to ${suggestion.targetSpaceName}!`);
        setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        onRefreshDashboard?.();
      } catch (e: any) {
        toast.show(e.message ?? "Failed to move items", "error");
      }
      setActing(null);
    } else {
      // For unlocated or no-target suggestions, navigate to inventory
      onNavigate("/inventory");
    }
  };

  const handleDismiss = (id: string) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    setSuggestions((prev) => prev.filter((s) => s.id !== id));
    try {
      localStorage.setItem("ht_reorg_dismissed", JSON.stringify([...next]));
    } catch {}
  };

  if (loading || suggestions.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-gray-900 flex items-center gap-2">
          <Shuffle className="w-4 h-4 text-primary" />
          Reorganize
        </h2>
        <span className="text-xs text-gray-400">{suggestions.length} suggestion{suggestions.length > 1 ? "s" : ""}</span>
      </div>

      <div className="space-y-2">
        {suggestions.slice(0, 3).map((suggestion) => {
          const Icon = typeIcons[suggestion.type];
          const colors = typeColors[suggestion.type];
          const iconBg = iconBgColors[suggestion.type];

          return (
            <div
              key={suggestion.id}
              className={cn(
                "relative p-4 rounded-2xl border transition-all",
                colors,
              )}
            >
              {/* Dismiss button */}
              <button
                onClick={() => handleDismiss(suggestion.id)}
                className="absolute top-3 right-3 p-1 rounded-full hover:bg-black/5 text-current opacity-40 hover:opacity-70 transition-opacity"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>

              <div className="flex items-start gap-3 pr-6">
                <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center shrink-0", iconBg)}>
                  <Icon className="w-4.5 h-4.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold leading-tight">{suggestion.title}</h3>
                  <p className="text-xs mt-0.5 opacity-75 leading-relaxed">{suggestion.description}</p>
                  <button
                    onClick={() => handleAction(suggestion)}
                    disabled={acting === suggestion.id}
                    className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/80 border border-current/20 text-xs font-medium hover:bg-white transition-colors disabled:opacity-50"
                  >
                    {acting === suggestion.id ? (
                      <span className="animate-pulse">Moving...</span>
                    ) : (
                      <>
                        {suggestion.actionLabel}
                        <ArrowRight className="w-3 h-3" />
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Item count badge — omitted for suggestions that move no items */}
              {suggestion.itemIds.length > 0 && (
                <div className="absolute bottom-3 right-3">
                  <span className="text-[10px] font-medium opacity-50">
                    {suggestion.itemIds.length} item{suggestion.itemIds.length !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
